/**
 * useGuidelineNames
 *
 * The Borrower Analysis home shows each loan's guideline by name ("Underwriting
 * 4.2"), but the job does not store which guideline it is reviewed against.
 * This loads the tenant's Borrower Analysis guidelines once so the page can
 * resolve a loan's remembered choice (see `resolveGuidelineName`). A failure
 * leaves the map empty and the column shows "—".
 *
 * Requirements: 2.5
 */

import { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { graphqlClient } from '../../services/graphqlClient';

/** Guideline id → name. Empty unless `enabled`. */
export function useGuidelineNames(enabled: boolean): ReadonlyMap<string, string> {
  const [names, setNames] = useState<ReadonlyMap<string, string>>(() => new Map());

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void (async () => {
      try {
        const session = await fetchAuthSession();
        const tenantId =
          (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
        const guidelines = await graphqlClient.queries.listGuidelines(tenantId, 'Borrower Analysis');
        if (!alive) return;
        setNames(new Map(guidelines.map((g) => [g.guidelineId, g.name])));
      } catch (err) {
        console.error('Failed to load guideline names:', err);
      }
    })();
    return () => {
      alive = false;
    };
  }, [enabled]);

  return names;
}
